import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { StatusLine } from "../../components/StatusLine";
import {
  useAuthoringRun,
  usePromotePromptVersion,
} from "../../hooks/useAuthoring";
import type {
  AuthoringReport,
  BreakdownRubric,
  Icp,
} from "../../lib/types";
import { IterationCard } from "./IterationCard";
import { PromptBlock } from "./PromptDiff";
import { STATUS_LABELS, formatTime, pluralize, statusVariant } from "./shared";

export function RunDetail({ icp, runId }: { icp: Icp; runId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, error } = useAuthoringRun(runId);
  const promote = usePromotePromptVersion();

  const run = data?.run;
  const versionForRun = data?.prompt_version ?? undefined;

  if (isLoading) {
    return (
      <section className="card">
        <StatusLine variant="running" message="Loading run…" />
      </section>
    );
  }

  if (isError || !run) {
    return (
      <section className="card">
        <StatusLine
          variant="error"
          message={`Could not load run: ${error?.message ?? "unknown"}`}
        />
      </section>
    );
  }

  const onPromote = async () => {
    if (!versionForRun) return;
    try {
      await promote.mutateAsync({ version_id: versionForRun.id });
      enqueueSnackbar(`Promoted v${versionForRun.version} to live prompt.`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(
        `Promote failed: ${err instanceof Error ? err.message : err}`,
        { variant: "error" },
      );
    }
  };

  const samples = run.sample_contacts ?? [];
  const positiveCount = samples.filter((entry) => entry.kind === "positive").length;
  const negativeCount = samples.filter((entry) => entry.kind === "negative").length;
  // Pipeline marks the iteration with fewest misclassifications as the
  // promoted one (see _pick_best_iteration); surface that on the card so
  // users understand why a non-final iteration may have been picked.
  const bestIteration = run.report?.final?.iteration;
  // ``min_score`` is captured on the report at run time. Show that — not
  // ``icp.min_score``, which may have changed after the run finished and
  // would mislabel the FN/FP threshold on each iteration card.
  const runMinScore = run.report?.min_score ?? icp.min_score;

  return (
    <section className="card">
      <h2>Authoring run — {icp.display_name}</h2>
      <p className="breadcrumb">
        <Link to={`/icps/${icp.name}/improve`}>← Back to improve-prompt</Link>{" "}
        · <Link to={`/icps/${icp.name}`}>ICP settings</Link>
      </p>

      <div className="chip-row" style={{ marginBottom: 12 }}>
        <span className="chip">
          Mode <strong>{run.mode}</strong>
        </span>
        <span className="chip">
          Run <code>{run.id}</code>
        </span>
        <span className="chip">
          {positiveCount} positive · {negativeCount} negative samples
        </span>
        <span className="chip">
          Iteration {run.current_iteration}/{run.max_iterations}
        </span>
        <span className="chip">Created {formatTime(run.created_at)}</span>
        {run.completed_at && (
          <span className="chip">Finished {formatTime(run.completed_at)}</span>
        )}
      </div>

      <StatusLine
        variant={statusVariant(run.status)}
        message={STATUS_LABELS[run.status] + (run.error ? ` — ${run.error}` : "")}
      />

      {run.run_instructions && (
        <div className="field" style={{ marginTop: 12 }}>
          <label>Run-specific instructions</label>
          <p
            className="rationale"
            style={{ whiteSpace: "pre-wrap", marginTop: 4 }}
          >
            {run.run_instructions}
          </p>
        </div>
      )}

      {run.report && <ReportSummary report={run.report} />}

      {run.mode !== "validate" && versionForRun && (
        <>
          {versionForRun.promoted_at && (
            <StatusLine
              variant={
                versionForRun.prompt === icp.prompt ? "success" : "idle"
              }
              message={
                versionForRun.prompt === icp.prompt
                  ? `Live as v${versionForRun.version} — promoted ${formatTime(
                    versionForRun.promoted_at,
                  )}.`
                  : `Promoted as v${versionForRun.version} on ${formatTime(
                    versionForRun.promoted_at,
                  )}, but the live prompt has changed since.`
              }
            />
          )}
          {versionForRun.prompt !== icp.prompt && (
            <div style={{ marginTop: 16 }}>
              <button onClick={onPromote} disabled={promote.isPending}>
                {promote.isPending
                  ? "Promoting…"
                  : versionForRun.promoted_at
                    ? `Re-promote v${versionForRun.version} to live prompt`
                    : `Promote v${versionForRun.version} to live prompt`}
              </button>
              <p className="card-desc" style={{ marginTop: 8 }}>
                This overwrites <code>{icp.name}.prompt</code> with this
                version. The live scoring pipeline picks up the new prompt on
                its next run.
              </p>
              <RubricChangePreview
                live={icp.breakdown_rubric}
                version={versionForRun.breakdown_rubric}
              />
            </div>
          )}
        </>
      )}

      {run.iterations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>
            {run.mode === "validate" ? "Result" : `Iterations (${run.iterations.length})`}
          </h3>
          {run.iterations.map((iteration, index) => (
            <IterationCard
              key={iteration.iteration}
              iteration={iteration}
              minScore={runMinScore}
              previousPrompt={
                index === 0
                  ? run.initial_prompt
                  : run.iterations[index - 1].prompt
              }
              previousLabel={index === 0 ? "initial prompt" : `iteration ${index}`}
              hidePromptPanels={run.mode === "validate"}
              isBest={
                run.mode !== "validate" &&
                iteration.iteration === bestIteration
              }
            />
          ))}
        </div>
      )}

      {run.mode !== "validate" && run.final_prompt && (
        <details style={{ marginTop: 24 }}>
          <summary>
            <strong>Final prompt</strong> ({run.final_prompt.length} chars)
          </summary>
          <PromptBlock text={run.final_prompt} />
        </details>
      )}
    </section>
  );
}

function RubricChangePreview({
  live,
  version,
}: {
  live: BreakdownRubric | null | undefined;
  version: BreakdownRubric | null | undefined;
}) {
  // Promotion copies the version's rubric onto icps.breakdown_rubric. When
  // the two differ the live scoring contract changes too — surface that here
  // so users don't notice only after seeing scores shift on the next run.
  const liveRubric: BreakdownRubric = live ?? {};
  const versionRubric: BreakdownRubric = version ?? {};
  const liveKeys = new Set(Object.keys(liveRubric));
  const versionKeys = new Set(Object.keys(versionRubric));
  const added = [...versionKeys].filter((key) => !liveKeys.has(key));
  const removed = [...liveKeys].filter((key) => !versionKeys.has(key));
  const modified = [...versionKeys].filter(
    (key) =>
      liveKeys.has(key) &&
      JSON.stringify(liveRubric[key]) !== JSON.stringify(versionRubric[key]),
  );
  if (added.length === 0 && removed.length === 0 && modified.length === 0) {
    return null;
  }
  return (
    <div className="chip-row" style={{ marginTop: 8 }}>
      <span className="chip chip-off" title="Rubric will also change">
        Rubric change
      </span>
      {added.map((key) => (
        <span key={`add-${key}`} className="chip chip-on">
          + {key}
        </span>
      ))}
      {removed.map((key) => (
        <span key={`rm-${key}`} className="chip chip-off">
          − {key}
        </span>
      ))}
      {modified.map((key) => (
        <span key={`mod-${key}`} className="chip">
          ≠ {key}
        </span>
      ))}
    </div>
  );
}

function ReportSummary({ report }: { report: AuthoringReport }) {
  const finalSummary = report.final;
  const iterations = report.iterations_count;
  const cost = report.estimated_cost_usd;
  if (!finalSummary) return null;
  return (
    <div className="chip-row" style={{ marginTop: 8 }}>
      <span className="chip chip-on">
        Matched {finalSummary.tp} {pluralize(finalSummary.tp, "positive")}
      </span>
      <span className="chip chip-on">
        Rejected {finalSummary.tn} {pluralize(finalSummary.tn, "negative")}
      </span>
      <span className={`chip ${finalSummary.fn > 0 ? "chip-off" : "chip-on"}`}>
        Missed {finalSummary.fn} {pluralize(finalSummary.fn, "positive")}
      </span>
      <span className={`chip ${finalSummary.fp > 0 ? "chip-off" : "chip-on"}`}>
        Wrongly matched {finalSummary.fp} {pluralize(finalSummary.fp, "negative")}
      </span>
      {typeof finalSummary.errors === "number" && finalSummary.errors > 0 && (
        <span
          className="chip chip-off"
          title="Scoring API errors — these samples didn't score this iteration."
        >
          ⚠ {finalSummary.errors} scoring {pluralize(finalSummary.errors, "error")}
        </span>
      )}
      {typeof iterations === "number" && (
        <span className="chip">
          {iterations} {pluralize(iterations, "iteration")}
        </span>
      )}
      {iterations !== 1 && (
        <span
          className="chip"
          title="Iteration with the fewest misclassifications — this is the prompt promotion uses."
        >
          ★ best: iteration {finalSummary.iteration}
        </span>
      )}
      {typeof cost === "number" && (
        <span className="chip">~${cost.toFixed(3)}</span>
      )}
    </div>
  );
}
